import { useEffect, useState, type KeyboardEvent } from "react";
import { PaymentMethodLabel, type PaymentMethod } from "@probetalab/contracts";
import { money, parseMoney } from "../lib/format.js";
import { Icon } from "./icons.js";
import { Button, Field, IconButton, Input, Money, Select } from "./ui.js";

/** A payment the receptionist is about to record (sent as `payment.record` once the order exists). */
export interface PaymentDraft {
  method: PaymentMethod;
  amount_cents: number;
  tendered_cents: number | null;
  reference: string | null;
}

const METHODS: PaymentMethod[] = ["efectivo", "tarjeta_debito", "tarjeta_credito", "transferencia", "credito_paciente"];

export function paymentsTotal(ps: PaymentDraft[]): { paid: number; credit: number; change: number } {
  let paid = 0;
  let credit = 0;
  let change = 0;
  for (const p of ps) {
    if (p.method === "credito_paciente") credit += p.amount_cents;
    else paid += p.amount_cents;
    if (p.tendered_cents !== null && p.tendered_cents > p.amount_cents) change += p.tendered_cents - p.amount_cents;
  }
  return { paid, credit, change };
}

/**
 * Split payments: any number of entries by method. Cash asks "¿Con cuánto paga?" and shows the change;
 * cards and transfers take a reference. Credit is recorded but never counts as paid (contract rule).
 */
/**
 * The line still in the inputs, not yet added with «Agregar». Parents save it with the list so a
 * typed payment is never lost (Very, 2026-11-02 18:45: «Guardar pago» stayed disabled because the
 * amount shown was only the placeholder). `touched` says whether anything was typed, so a parent
 * that may legitimately save no payment (reception) can ignore an untouched line.
 */
export interface PendingPayment {
  draft: PaymentDraft;
  touched: boolean;
}

export function PaymentEditor({
  due,
  payments,
  onChange,
  onPending,
  onEnter,
  addLabel = "Agregar",
}: {
  due: number;
  payments: PaymentDraft[];
  onChange: (p: PaymentDraft[]) => void;
  onPending?: (p: PendingPayment | null) => void;
  /** Enter in any payment input: the parent's main action (save), so «Agregar» is not a required step. */
  onEnter?: () => void;
  /** Label of the button that adds the line to the list (splitting into several methods). */
  addLabel?: string;
}) {
  const [method, setMethod] = useState<PaymentMethod>("efectivo");
  // null = untouched: the field shows the balance and follows it. Typing less than the balance is
  // a partial payment (Fer, 2026-11-03 08:08: no «Abono parcial» toggle); empty disables saving.
  const [amount, setAmount] = useState<string | null>(null);
  const [tendered, setTendered] = useState("");
  const [reference, setReference] = useState("");
  const { paid } = paymentsTotal(payments);
  const remaining = Math.max(0, due - paid);
  const typedAmount = amount === null ? remaining : parseMoney(amount);
  // Cash typed above what is owed is what the patient hands over, not the payment: the payment is
  // the balance and the rest is change (Fernanda, 2026-11-02 19:43, typed 1000 on a $430 order).
  const overpaidCash = method === "efectivo" && typedAmount !== null && typedAmount > remaining && remaining > 0;
  const amountCents = overpaidCash ? remaining : typedAmount;
  const tenderedCents = overpaidCash && tendered.trim() === "" ? typedAmount : parseMoney(tendered);
  const isCash = method === "efectivo";
  const change = isCash && tenderedCents !== null && amountCents !== null ? Math.max(0, tenderedCents - amountCents) : 0;
  // Non-cash above the balance is refused (cash above it is change, handled above).
  const overBalance = !isCash && typedAmount !== null && typedAmount > remaining && remaining > 0;
  const partialDue = amountCents !== null && amountCents > 0 && amountCents < remaining && !overBalance ? remaining - amountCents : 0;
  const canAdd = amountCents !== null && amountCents > 0 && !overBalance && (!isCash || tenderedCents === null || tenderedCents >= amountCents);

  const touched = amount !== null || tendered.trim() !== "" || reference.trim() !== "";
  const open = remaining > 0 || payments.length === 0;
  const pending: PendingPayment | null =
    open && canAdd && amountCents !== null
      ? { draft: { method, amount_cents: amountCents, tendered_cents: isCash && tenderedCents !== null ? tenderedCents : null, reference: reference.trim() || null }, touched }
      : null;
  const pendingKey = pending ? JSON.stringify(pending) : "";
  useEffect(() => {
    onPending?.(pending);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingKey]);

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (onEnter) onEnter();
    else add();
  }

  function add() {
    if (!canAdd || amountCents === null) return;
    onChange([
      ...payments,
      {
        method,
        amount_cents: amountCents,
        tendered_cents: isCash && tenderedCents !== null ? tenderedCents : null,
        reference: reference.trim() || null,
      },
    ]);
    setAmount(null);
    setTendered("");
    setReference("");
  }

  return (
    <div className="stack">
      {payments.length > 0 && (
        <div className="pay-list">
          {payments.map((p, i) => (
            <div key={i} className="pay-item">
              <Icon name={p.method === "efectivo" ? "cash" : p.method === "credito_paciente" ? "clock" : "tag"} style={{ color: "var(--ink-3)" }} />
              <span className="grow">
                <b>{PaymentMethodLabel[p.method]}</b>
                {p.reference && <span className="muted small"> · Ref. {p.reference}</span>}
                {p.tendered_cents !== null && p.tendered_cents > p.amount_cents && (
                  <span className="muted small">
                    {" "}
                    · paga con {money(p.tendered_cents)}, cambio {money(p.tendered_cents - p.amount_cents)}
                  </span>
                )}
              </span>
              <Money cents={p.amount_cents} />
              <IconButton icon="x" label="Quitar pago" size="sm" onClick={() => onChange(payments.filter((_, j) => j !== i))} />
            </div>
          ))}
        </div>
      )}
      {remaining > 0 || payments.length === 0 ? (
        <div className="stack">
          <div className="pay-row">
            <Field label="Forma de pago">
              <Select value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
                {METHODS.map((m) => (
                  <option key={m} value={m}>
                    {PaymentMethodLabel[m]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Monto">
              <Input
                inputMode="decimal"
                value={amount ?? money(remaining)}
                onChange={(e) => setAmount(e.target.value)}
                onFocus={(e) => e.target.select()}
                onKeyDown={onKey}
                placeholder={money(remaining)}
                aria-label="Monto a pagar"
                aria-invalid={overBalance || undefined}
              />
            </Field>
            {isCash ? (
              <Field label="Paga con" optional>
                <Input inputMode="decimal" value={tendered} onChange={(e) => setTendered(e.target.value)} onKeyDown={onKey} placeholder="Efectivo recibido" />
              </Field>
            ) : (
              <Field label="Referencia" optional>
                <Input value={reference} onChange={(e) => setReference(e.target.value)} onKeyDown={onKey} placeholder={method === "transferencia" ? "Folio de transferencia" : "Voucher"} />
              </Field>
            )}
            <Button variant="secondary" icon="plus" onClick={add} disabled={!canAdd} title="Agregar pago">
              {addLabel}
            </Button>
          </div>
          <div className="pay-meta">
            {isCash && change > 0 ? <span className="pay-change">Cambio: {money(change)}</span> : <span />}
            {partialDue > 0 && (
              <span className="small" style={{ color: "var(--amber)", fontWeight: 600 }}>
                Abono parcial · queda adeudo de {money(partialDue)}
              </span>
            )}
            {overBalance && <span className="small" style={{ color: "var(--red)", fontWeight: 600 }}>El monto no puede ser mayor que el adeudo ({money(remaining)}).</span>}
          </div>
          {method === "credito_paciente" && <p className="small muted">El crédito queda registrado, pero el adeudo sigue vigente hasta que se liquide.</p>}
        </div>
      ) : (
        <p className="small" style={{ color: "var(--brand-ground)", fontWeight: 600 }}>
          Liquidado.
        </p>
      )}
    </div>
  );
}
